import { Menu, MenuItem, PopoverPosition, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  getCurrentFieldWithAnimalsFromArray,
  useField,
  useFields,
  useUpdateField,
} from "model";

import { AnalyticsEvents, mixpanel } from "common/analytics";

import { useNotification } from "app/NotificationProvider";
import { useTranslation } from "common/locales";
import { moveAnimalsIn } from "features/singleCoverInput/moveAnimalsIn";
import { useMemo } from "react";
import { useUIStore } from "stores";
interface FieldAnimalGroupActionMenuProps {
  fieldId: string;
  anchor?: null | HTMLElement;
  anchorPosition?: PopoverPosition;
  close: () => void;
}

export default function FieldAnimalGroupActionMenu({
  fieldId,
  anchor,
  anchorPosition,
  close,
}: FieldAnimalGroupActionMenuProps) {
  // Use React Query to fetch field data
  const { data: currentField } = useField(fieldId);
  const { t } = useTranslation();
  const { notify } = useNotification();

  // Use Zustand for dialog state
  const openMoveOutEventDialog = useUIStore(
    (state) => state.openMoveOutEventDialog
  );

  // Use React Query mutation for field updates
  const updateFieldMutation = useUpdateField();

  // Use React Query for fields data to find current field with animals
  const { data: allFields = [] } = useFields();
  const currentFieldWithAnimals = useMemo(
    () =>
      getCurrentFieldWithAnimalsFromArray(allFields, currentField?.animalGroup),
    [allFields, currentField?.animalGroup]
  );

  const canMoveIn =
    currentField?.animalGroup && fieldId !== currentFieldWithAnimals?.guid;

  async function onMoveIn() {
    close();
    try {
      if (currentField) {
        if (currentFieldWithAnimals) {
          openMoveOutEventDialog({
            open: true,
            newFieldWithAnimals: currentField,
            currentFieldWithAnimals: currentFieldWithAnimals,
          });
        } else {
          await moveAnimalsIn(
            currentField,
            currentFieldWithAnimals,
            new Date(),
            notify,
            t
          );
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function moveToSilage() {
    if (currentField) {
      close();
      try {
        await updateFieldMutation.mutateAsync({
          ...currentField,
          animalGroup: undefined,
        });
        mixpanel.track(AnalyticsEvents.FIELD_ASSIGNED);
        notify(
          t("fieldlistmenu.notification.movedToSilage", {
            fieldName: currentField?.name,
          })
        );
      } catch (e) {
        mixpanel.track(AnalyticsEvents.FIELD_ASSIGN_FAILED);
        notify(t("fieldlistmenu.notification.moveFailed"), "error");
      }
    }
  }

  return (
    <div>
      <Menu
        id="simple-menu"
        anchorEl={anchor}
        anchorPosition={anchorPosition}
        anchorReference={
          anchor ? "anchorEl" : anchorPosition ? "anchorPosition" : "none"
        }
        keepMounted
        open={Boolean(anchor || anchorPosition)}
        onClose={close}
      >
        <MoveAnimalsMenuItem
          canMoveIn={!!canMoveIn}
          fieldHasAnimalGroup={!!currentField?.animalGroup}
          onMoveIn={onMoveIn}
        />
        <UnassignFieldFromAnimalGroupItem
          onUnassignFieldFromAnimalGroup={moveToSilage}
        />
      </Menu>
    </div>
  );
}

const TooltipChild = styled("div")({
  display: "inline-block",
});

interface UnassignFieldFromAnimalGroupItemProps {
  onUnassignFieldFromAnimalGroup: () => Promise<void>;
}

function UnassignFieldFromAnimalGroupItem({
  onUnassignFieldFromAnimalGroup,
}: UnassignFieldFromAnimalGroupItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItem onClick={onUnassignFieldFromAnimalGroup}>
      {t("fieldlistmenu.silage.label")}
    </MenuItem>
  );
}

interface MoveAnimalsMenuItemProps {
  canMoveIn: boolean;
  onMoveIn: () => Promise<void>;
  fieldHasAnimalGroup: boolean;
}

function MoveAnimalsMenuItem({
  canMoveIn,
  onMoveIn,
  fieldHasAnimalGroup,
}: MoveAnimalsMenuItemProps) {
  const { t } = useTranslation();
  const disabled = !canMoveIn;

  const MoveMenuItem = (
    <MenuItem disabled={disabled} onClick={!disabled ? onMoveIn : undefined}>
      {t("fieldlistmenu.moveIn.label")}
    </MenuItem>
  );

  return canMoveIn ? (
    MoveMenuItem
  ) : (
    <Tooltip
      title={
        fieldHasAnimalGroup
          ? t("fieldlistmenu.moveIn.tooltip.sameField")
          : t("fieldlistmenu.moveIn.tooltip.silage")
      }
    >
      <TooltipChild>{MoveMenuItem}</TooltipChild>
    </Tooltip>
  );
}
